import type { Resource } from "@eai-toolkit/client";
import { ResourceApi } from "@eai-toolkit/client";

import { baseApi, handleQueryError } from "./baseApi";
import { axiosInstance } from "./utils";

export type { Resource as ResourceMetadata };

const resourceApiClient = new ResourceApi(undefined, undefined, axiosInstance);

/**
 * Min-interval rate limiter for the metadata endpoint. The backend throttles
 * this endpoint to protect against scraping, so we space outbound requests
 * at most one per `METADATA_MIN_INTERVAL_MS`. If a request arrives within
 * the interval it waits the remainder. If multiple requests are waiting,
 * only the most recent one proceeds — earlier ones are rejected.
 */
const METADATA_MIN_INTERVAL_MS = 2_000;
let lastSentAt = 0;
let pending: {
  timer: ReturnType<typeof setTimeout>;
  reject: (r: unknown) => void;
} | null = null;

export async function rateLimitMetadata(signal: AbortSignal): Promise<void> {
  // Supersede any previous waiting request
  if (pending) {
    clearTimeout(pending.timer);
    pending.reject(new DOMException("Superseded", "AbortError"));
    pending = null;
  }

  const wait = Math.max(0, lastSentAt + METADATA_MIN_INTERVAL_MS - Date.now());
  if (wait > 0) {
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        pending = null;
        resolve();
      }, wait);
      pending = { timer, reject };
      signal.addEventListener(
        "abort",
        () => {
          clearTimeout(timer);
          pending = null;
          reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
        },
        { once: true },
      );
    });
  }

  lastSentAt = Date.now();
}

export const resourceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getResourceMetadata: builder.query<Resource | null, { identifier: string }>(
      {
        queryFn: async ({ identifier }, api) => {
          try {
            await rateLimitMetadata(api.signal);
            const { data } = await resourceApiClient.v1ResourceMetadataGet({
              resourceId: identifier,
            });
            return { data };
          } catch (error) {
            // 404 means resource not found — return null instead of an error
            if (
              error &&
              typeof error === "object" &&
              "response" in error &&
              (error as { response?: { status?: number } }).response?.status ===
                404
            ) {
              return { data: null };
            }
            return handleQueryError(error);
          }
        },
      },
    ),
  }),
});

export const { useGetResourceMetadataQuery } = resourceApi;
